import React from 'react';
import { Phone, MapPin, Heart, Facebook } from 'lucide-react';
import { Link } from 'react-router-dom';
import { bakeryInfo } from '../data/mock';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#1e1919] py-12 sm:py-16 pb-28 lg:pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12">
          {/* Brand Column */}
          <div className="sm:col-span-2 lg:col-span-2">
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <h3 className="font-bold text-white text-lg sm:text-xl">{bakeryInfo.name}</h3>
            </div>
            <p className="text-[#bbb5ae] text-sm sm:text-base leading-relaxed max-w-md mb-4 sm:mb-6">
              Ihre traditionelle Wiener Bäckerei und Konditorei. 
              Wir verwenden nur die besten Zutaten für unsere handgefertigten 
              Backwaren – jeden Tag frisch für Sie.
            </p>
            
            {/* Social Media */}
            <div className="flex items-center gap-3 sm:gap-4">
              <a
                href="https://www.facebook.com/profile.php?id=100063518740394"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 sm:w-10 h-9 sm:h-10 bg-[#2a2525] rounded-full flex items-center justify-center text-[#bbb5ae] hover:bg-[#1877f2] hover:text-white transition-all duration-300"
                aria-label="Facebook"
              >
                <Facebook className="w-4 sm:w-5 h-4 sm:h-5" />
              </a>
              <a
                href="https://maps.app.goo.gl/spTRAUnRWC8BRPht8"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 sm:w-10 h-9 sm:h-10 bg-[#2a2525] rounded-full flex items-center justify-center text-[#bbb5ae] hover:bg-[#4285f4] hover:text-white transition-all duration-300"
                aria-label="Google Maps"
              >
                <MapPin className="w-4 sm:w-5 h-4 sm:h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold mb-4 sm:mb-6 text-sm sm:text-base">Navigation</h4>
            <ul className="space-y-2 sm:space-y-3">
              {[
                { href: '#ueber-uns', label: 'Über Uns' },
                { href: '#services', label: 'Services' },
                { href: '#produkte', label: 'Produkte' },
                { href: '#torten', label: 'Torten' },
                { href: '#bewertungen', label: 'Bewertungen' },
                { href: '#kontakt', label: 'Kontakt' }
              ].map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-[#bbb5ae] text-sm sm:text-base hover:text-white transition-colors duration-300"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold mb-4 sm:mb-6 text-sm sm:text-base">Kontakt</h4>
            <ul className="space-y-3 sm:space-y-4">
              <li>
                <a
                  href={`tel:${bakeryInfo.phone}`}
                  className="flex items-center gap-2 sm:gap-3 text-[#bbb5ae] text-sm sm:text-base hover:text-[#fad24b] transition-colors duration-300"
                >
                  <Phone className="w-4 sm:w-5 h-4 sm:h-5" />
                  {bakeryInfo.phone}
                </a>
              </li>
              <li className="flex items-start gap-2 sm:gap-3 text-[#bbb5ae] text-sm sm:text-base">
                <MapPin className="w-4 sm:w-5 h-4 sm:h-5 flex-shrink-0 mt-0.5" />
                {bakeryInfo.address}
              </li>
              <li>
                <a
                  href="https://www.facebook.com/profile.php?id=100063518740394"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 sm:gap-3 text-[#bbb5ae] text-sm sm:text-base hover:text-[#1877f2] transition-colors duration-300"
                >
                  <Facebook className="w-4 sm:w-5 h-4 sm:h-5" />
                  Facebook
                </a>
              </li>
            </ul>

            {/* Badge */}
            <div className="mt-4 sm:mt-6 inline-flex items-center gap-2 bg-[#2a2525] rounded-full px-3 sm:px-4 py-1.5 sm:py-2">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-white text-xs sm:text-sm">365 Tage geöffnet</span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-[#2a2525] pt-6 sm:pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
            <p className="text-[#736c64] text-xs sm:text-sm text-center sm:text-left">
              © {currentYear} {bakeryInfo.name}. Alle Rechte vorbehalten.
            </p>
            <div className="flex items-center gap-4 sm:gap-6">
              <Link to="/impressum" className="text-[#736c64] text-xs sm:text-sm hover:text-white transition-colors duration-300" data-testid="footer-impressum-link">
                Impressum
              </Link>
              <Link to="/datenschutz" className="text-[#736c64] text-xs sm:text-sm hover:text-white transition-colors duration-300" data-testid="footer-datenschutz-link">
                Datenschutz
              </Link>
              <p className="text-[#736c64] text-xs sm:text-sm flex items-center gap-1">
                Mit <Heart className="w-3 sm:w-4 h-3 sm:h-4 text-[#ff8c19]" /> gemacht in Wien
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
